import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ShieldCheckIcon, RefreshCwIcon, DatabaseIcon, GaugeIcon, CodeIcon, LayoutIcon, ShoppingCartIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteOnderhoudPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website onderhoud | Altijd up-to-date | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website onderhoud is essentieel voor veiligheid en prestaties. Leer waarom regelmatig onderhoud nodig is en wat het kost. Tips van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website onderhoud | Altijd up-to-date | Noord',
      'og:description': 'Website onderhoud is essentieel voor veiligheid en prestaties. Leer waarom regelmatig onderhoud nodig is en wat het kost.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-onderhoud',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe vaak heeft mijn website onderhoud nodig?',
    a: "Wij voeren wekelijks controles uit op je website. Kritieke beveiligingsupdates worden direct doorgevoerd. Reguliere updates van WordPress, thema's en plugins voeren wij maandelijks uit na een grondige test. Zo blijft je website altijd veilig en up-to-date."
  }, {
    q: 'Wat kost website onderhoud?',
    a: 'De kosten voor website onderhoud hangen af van de complexiteit van je website en het gewenste serviceniveau. Wij bieden transparante onderhoudspakketten aan zodat je vooraf precies weet waar je aan toe bent. Neem contact op voor een vrijblijvende offerte.'
  }, {
    q: 'Is website onderhoud verplicht?',
    a: 'Technisch gezien niet, maar het wordt sterk aangeraden. Zonder regelmatig onderhoud loop je risico op beveiligingslekken, trage laadtijden en compatibiliteitsproblemen. Een goed onderhouden website presteert beter in Google en biedt je bezoekers een betere ervaring.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2854">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2855">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2856">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2857" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2858" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2859" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2860">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2861">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2862">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2863">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2864">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2865">/</span>
            <span className="text-accent-light font-medium" data-id="element-2866">
              Website onderhoud
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2867">
            Website onderhoud voor bedrijven
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2868">
            Zorg dat je website veilig en snel blijft. Wij nemen het technisch
            onderhoud volledig uit handen zodat jij je kunt focussen op
            ondernemen.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2869">
            <a href="#onderhoud-info" onClick={e => scrollToSection(e, 'onderhoud-info')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2870">
              Bekijk onderhoud
              <ArrowRightIcon className="w-5 h-5" data-id="element-2871" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom onderhoud belangrijk is */}
      <section id="onderhoud-info" className="py-24 bg-surface-muted" data-id="element-2872">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2873">
          <div className="text-center mb-16" data-id="element-2874">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2875">
              Waarom website onderhoud belangrijk is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2876">
              Een website zonder onderhoud is kwetsbaar. Met onze website
              onderhoud service blijft jouw site veilig, snel en betrouwbaar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2877">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2878">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2879">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-2880" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2881">
                Beveiliging
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2882">
                Regelmatig onderhoud beschermt je website tegen hackers, malware
                en beveiligingslekken.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2883">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2884">
                <RefreshCwIcon className="w-6 h-6 text-accent" data-id="element-2885" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2886">Updates</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2887">
                WordPress, thema's en plugins moeten up-to-date blijven voor
                optimale werking en veiligheid.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2888">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2889">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-2890" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2891">Backups</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2892">
                Dagelijkse backups zorgen ervoor dat je website altijd hersteld
                kan worden bij onvoorziene problemen.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2893">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2894">
                <GaugeIcon className="w-6 h-6 text-accent" data-id="element-2895" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2896">
                Performance
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2897">
                Optimalisatie van laadsnelheid en database zorgt voor een snelle
                website en betere vindbaarheid.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat wij onderhouden */}
      <section className="py-24 bg-white" data-id="element-2898">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2899">
          <div className="text-center mb-16" data-id="element-2900">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2901">
              Onderhoud voor elk type website
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2902">
              Van kleine lokale ondernemers tot middelgrote bedrijven. Wij
              houden je site in topconditie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2903">
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors text-center" data-id="element-2904">
              <div className="w-14 h-14 rounded-xl bg-navy-50 flex items-center justify-center mb-6 mx-auto" data-id="element-2905">
                <CodeIcon className="w-7 h-7 text-navy-700" data-id="element-2906" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2907">
                WordPress websites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2908">
                Onderhoud van WordPress websites inclusief core updates, plugin
                updates en thema-onderhoud. Wij zijn gespecialiseerd in
                WordPress onderhoud.
              </p>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors text-center" data-id="element-2909">
              <div className="w-14 h-14 rounded-xl bg-navy-50 flex items-center justify-center mb-6 mx-auto" data-id="element-2910">
                <LayoutIcon className="w-7 h-7 text-navy-700" data-id="element-2911" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2912">
                Bedrijfswebsites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2913">
                Professioneel onderhoud voor bedrijfswebsites. Van kleine
                ondernemers tot middelgrote bedrijven — wij houden je site in
                topvorm.
              </p>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors text-center" data-id="element-2914">
              <div className="w-14 h-14 rounded-xl bg-navy-50 flex items-center justify-center mb-6 mx-auto" data-id="element-2915">
                <ShoppingCartIcon className="w-7 h-7 text-navy-700" data-id="element-2916" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2917">Webshops</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2918">
                Onderhoud van WooCommerce webshops met extra aandacht voor
                beveiliging, betalingsintegraties en productpagina's.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-2919">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2920">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2921">
            Gerelateerde pagina's:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2922">
            <a href="#/wordpress-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2923">
              WordPress onderhoud
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2924">•</span>
            <a href="#/website-hosting" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2925">
              Website hosting
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2926">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2927">
              Website beveiliging
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-2928">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2929">
          <div className="text-center mb-16" data-id="element-2930">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2931">
              Veelgestelde vragen over website onderhoud
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2932">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-2933">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2934">
                  <span className="font-bold text-navy-900" data-id="element-2935">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2936" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2937" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2938">
                    {faq.a}
                  </div>}
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-2939">
            <a href="#contact" onClick={e => scrollToSection(e, 'contact')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-semibold px-6 py-3 rounded-button shadow-soft hover:shadow-medium transition-all duration-200 group" data-id="element-2940">
              Nog vragen? Neem contact op
              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" data-id="element-2941" />
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2942" />
    </main>;
}